<template>
  <v-container class="grey lighten-5">
    <h2>Sync-Probleme</h2>
    <br/>

    <v-card style="margin-bottom: 24px">
      <v-card-text>
        <v-row>
          <v-col cols="12" md="4">
            <v-select
                v-model="selectedCompanyId"
                :items="companyItems"
                clearable
                label="Firma"
                item-text="name"
                item-value="id"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
                v-model="searchTerm"
                label="Suche nach Nutzer, Referenz oder Detail"
            />
          </v-col>
          <v-col cols="12" md="2">
            <v-select
                v-model="limit"
                :items="[50, 100, 200, 500]"
                label="Anzahl"
            />
          </v-col>
          <v-col cols="12" md="2" class="d-flex align-center">
            <v-btn color="primary" :loading="loading" @click="loadData">
              Aktualisieren
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-data-table
        :headers="headers"
        :items="issues"
        :items-per-page="15"
        :loading="loading"
        :item-class="issueRowClass"
        class="elevation-1"
    >
      <template v-slot:item.status="{ item }">
        <v-chip :color="issueSeverity(item).color" dark small>
          {{ issueSeverity(item).label }}
        </v-chip>
      </template>

      <template v-slot:item.reason="{ item }">
        <v-chip :color="issueReason(item).color" dark small>
          {{ issueReason(item).label }}
        </v-chip>
      </template>

      <template v-slot:item.date="{ item }">
        {{ item.date | moment("DD.MM.YYYY HH:mm:ss") }}
      </template>

      <template v-slot:item.companyName="{ item }">
        {{ item.companyName || item.companyId || "-" }}
      </template>

      <template v-slot:item.courseTitle="{ item }">
        {{ item.courseTitle || item.courseId || "-" }}
      </template>

      <template v-slot:item.note="{ item }">
        <div style="max-width: 640px; white-space: normal; word-break: break-word;">
          <div v-for="entry in issueDetails(item)" :key="entry.key" style="margin-bottom: 4px;">
            <strong>{{ entry.key }}:</strong> {{ entry.value }}
          </div>
        </div>
      </template>
    </v-data-table>

    <v-snackbar :timeout="5000" :value="error" color="error" left shaped top app>
      Fehler beim Laden der Sync-Probleme.
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: "SuperAdminSyncIssues",
  data: () => ({
    companies: [],
    error: false,
    issues: [],
    limit: 200,
    loading: false,
    searchTerm: "",
    selectedCompanyId: null,
    headers: [
      {text: "Ampel", value: "status"},
      {text: "Grund", value: "reason"},
      {text: "Zeitpunkt", value: "date"},
      {text: "Nutzer", value: "username"},
      {text: "Firma", value: "companyName"},
      {text: "Kurs", value: "courseTitle"},
      {text: "Referenz", value: "referenceId"},
      {text: "Details", value: "note"},
    ],
  }),
  computed: {
    companyItems() {
      return this.companies.map((company) => ({
        id: company.id,
        name: company.name,
      }));
    },
  },
  methods: {
    parseNote(item) {
      return (item.note || "").split("|").map((entry) => entry.trim()).filter((entry) => entry.length > 0).reduce((result, entry) => {
        const separatorIndex = entry.indexOf("=");
        if (separatorIndex === -1) {
          result.misc = result.misc ? `${result.misc} | ${entry}` : entry;
          return result;
        }

        const key = entry.slice(0, separatorIndex);
        const value = entry.slice(separatorIndex + 1);
        result[key] = value;
        return result;
      }, {});
    },
    issueReason(item) {
      const reason = this.parseNote(item).reason || "unknown";
      const mapping = {
        progress_request_failed: {label: "Request fehlgeschlagen", color: "red darken-2"},
        sync_stale: {label: "Sync zu alt", color: "orange darken-2"},
        scorm_connection_late: {label: "SCORM startet nicht", color: "orange darken-3"},
        state_capture_failed: {label: "State nicht lesbar", color: "deep-orange darken-2"},
        empty_scorm_state: {label: "State leer", color: "amber darken-3"},
      };

      return mapping[reason] || {label: reason, color: "blue-grey darken-1"};
    },
    issueSeverity(item) {
      const reason = this.parseNote(item).reason || "unknown";
      if (reason === "progress_request_failed" || reason === "state_capture_failed") {
        return {label: "Rot", color: "red darken-2"};
      }
      if (reason === "sync_stale" || reason === "scorm_connection_late") {
        return {label: "Gelb", color: "orange darken-2"};
      }
      if (reason === "empty_scorm_state") {
        return {label: "Gelb", color: "amber darken-3"};
      }
      return {label: "Info", color: "blue-grey darken-1"};
    },
    issueDetails(item) {
      const parsed = this.parseNote(item);
      return Object.entries(parsed)
          .filter(([key]) => key !== "reason")
          .map(([key, value]) => ({
            key: key,
            value: value,
          }));
    },
    issueRowClass(item) {
      const reason = this.parseNote(item).reason || "unknown";
      if (reason === "progress_request_failed" || reason === "state_capture_failed") {
        return "sync-issue-critical";
      }
      if (reason === "sync_stale" || reason === "scorm_connection_late" || reason === "empty_scorm_state") {
        return "sync-issue-warning";
      }
      return "";
    },
    loadCompanies() {
      this.$httpCompanyApi.all((companies) => {
        this.companies = companies;
      }, () => {
        this.companies = [];
      });
    },
    loadData() {
      this.loading = true;
      this.$httpLogApi.progressSyncIssues({
        companyId: this.selectedCompanyId,
        searchTerm: this.searchTerm,
        limit: this.limit,
      }, (result) => {
        this.issues = result;
        this.loading = false;
      }, () => {
        this.loading = false;
        this.error = true;
        setTimeout(() => this.error = false, 3000);
      });
    },
  },
  mounted() {
    this.loadCompanies();
    this.loadData();
  }
}
</script>

<style scoped>
.sync-issue-critical {
  background: rgba(183, 28, 28, 0.08);
}

.sync-issue-warning {
  background: rgba(245, 124, 0, 0.08);
}
</style>
